import { useMemo } from "react";
import { SubtaskSection } from "@/components/board/SubtaskSection";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import {
  closedOptionId,
  openOptionId,
  subtaskCheckboxDisabled,
  workflowStatusColumn,
  type SubtaskBoard,
} from "@/lib/subtasks";
import type { ViewBoard, ViewRow } from "@/lib/boardView";
import type { CellValue, ColumnDef, Row, UUID } from "@/lib/types";

/**
 * The Unteraufgaben section, wired to the board — used by BOTH the desktop
 * `RowDetailPanel` and the mobile `RowDetailSheet`.
 *
 * 🔴 It lives in ONE file on purpose. A control that exists only on the desktop panel does
 * not exist on a phone, and this repo has shipped that omission before. Duplicating the gate,
 * the type seam and the five handlers into two components is exactly how the two surfaces
 * drift apart; here they cannot.
 *
 * ─── 🔴 THE TYPE SEAM ────────────────────────────────────────────────────────────────────
 * `lib/subtasks.ts` and `SubtaskSection` work on the RAW WIRE objects: a `ColumnDef` keeps
 * its status options under `config.options`, a `Row` keeps a flat `cells[]`. What a detail
 * panel holds is a `ViewBoard`: its `ViewColumn` has FLATTENED `.options` (and no `config`
 * at all) and parks the wire object at `.raw`; its `ViewRow` is the projected shape with the
 * wire row at `.raw`.
 *
 * Hand a ViewColumn straight to `workflowStatusColumn()` and `config.options` is `undefined`
 * → no option carries a role → it finds NO workflow column → no checkbox, no done-ness, no
 * rollup — and NOTHING throws. A silent, type-checked no-op. So the seam is crossed exactly
 * once, here, explicitly: `.raw` in, raw out. Everything below this line is wire types.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 */
interface RowSubtasksProps {
  /** The FULL board — never a filtered/column-hidden display copy (see `fullBoard` below). */
  board: ViewBoard;
  /** The parent row whose detail is open. */
  row: ViewRow;
  /** The SAME cell-write path `DetailColumnEditor` uses (BoardPage → useBoard.updateCell). */
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  /** Mint a child row + write its title/assignee in one go (BoardPage → useBoard.createRow). */
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  /** Open a sub-task's own detail. */
  onOpenRow?: (rowId: string) => void;
}

function firstRawColumn(columns: ColumnDef[], type: ColumnDef["type"]): ColumnDef | null {
  return columns.find((c) => c.type === type) ?? null;
}

/**
 * 🔴 THE GATE — a component of its own, holding NO hooks, because both of its rules must be
 * decided BEFORE any hook (and any network query) runs.
 *
 *  1. Aufgaben boards ONLY (v1). The section is generic and its ticket-safety predicate is
 *     already correct, but widening it to ticket/project boards is a decision to make AFTER
 *     the Protokoll users have worn a groove in it — not before.
 *  2. TOP-LEVEL rows only. The backend caps the tree at MAX_ROW_DEPTH = 1, so a create under
 *     a row that is already a child is a 422 (DepthCapExceededError). The section used to
 *     render on a sub-task's OWN panel, offering a create box that could never succeed: the
 *     input cleared and the typed title vanished. A control that cannot succeed is not shown.
 *
 * The people directory (`useBoardPeople`, a network query) therefore lives in the INNER
 * component — it fires where the section actually renders, not on every row-detail open on
 * every board in the app.
 */
export function RowSubtasks(props: RowSubtasksProps) {
  const parentRow = props.row.raw ?? null;
  if (!props.board.is_aufgaben || !parentRow || parentRow.depth > 0) return null;
  return <RowSubtasksSection {...props} parentRow={parentRow} />;
}

function RowSubtasksSection({
  board,
  row,
  parentRow,
  onPatchCell,
  onCreateSubtask,
  onOpenRow,
}: RowSubtasksProps & { parentRow: Row }) {
  // ── The seam. `.raw` or nothing: a ViewColumn/ViewRow must never reach lib/subtasks. ──
  const subtaskBoard = useMemo<SubtaskBoard>(
    () => ({
      kind: board.kind,
      // adaptBoard already sorted the columns by position, so "the first text column" here
      // is the same column the table and DetailColumnEditor call the title.
      columns: board.columns.map((c) => c.raw).filter((c): c is ColumnDef => Boolean(c)),
    }),
    [board.kind, board.columns],
  );

  // The board already holds every row — a sub-task is just a row with parent_row_id set.
  // (Soft-deleted rows never reach the client: the board endpoint filters them, and `Row`
  // has no `deleted_at` field to filter on.)
  const subtasks = useMemo<Row[]>(
    () =>
      board.rows
        .filter((r) => r.parentRowId === row.id)
        .map((r) => r.raw)
        .filter((r): r is Row => Boolean(r))
        .sort((a, b) => a.position - b.position),
    [board.rows, row.id],
  );

  // 🔴 The BOARD's people directory — workspace members PLUS cross-department guests — the
  // same one every other person surface resolves names from (EditablePersonField). The
  // workspace-members list alone cannot render a guest: a sub-task delegated across
  // departments (the spec's whole delegation case) reads "Niemand" and cannot be picked, and
  // a guest opening this panel from Home 404s on /workspaces/{id}/members and sees EVERY
  // assignee as "Niemand".
  const { data: people = [] } = useBoardPeople(board.id as UUID);

  const statusCol = workflowStatusColumn(subtaskBoard);
  const titleCol = firstRawColumn(subtaskBoard.columns, "text");
  const personCol = firstRawColumn(subtaskBoard.columns, "person");
  const dateCol = firstRawColumn(subtaskBoard.columns, "date");

  // A viewer/guest sees the sub-tasks; they do not get a checkbox, a picker or a create box.
  // 🔴 The BOARD's can_write is the rule — never `Boolean(onPatchCell)`: every surface hands
  // the panel that callback, so a viewer would get live controls. Without a cell-write path
  // (fixture mode) nothing is editable either.
  const canWrite = board.canWrite && Boolean(onPatchCell);

  // Every handler NO-OPS when its column is absent — a board can be missing any of them, and
  // a missing column must never throw or write to the wrong one.
  return (
    <SubtaskSection
      board={subtaskBoard}
      parentRow={parentRow}
      subtasks={subtasks}
      members={people}
      canWrite={canWrite}
      onCreate={(title, assigneeId) => onCreateSubtask?.(row.id as UUID, title, assigneeId)}
      onToggleDone={(rowId, done) => {
        if (!statusCol) return;
        // Belt AND braces behind SubtaskSection's read-only pill: on a ticket board the
        // workflow status is ACTION-only (comment + recipient + notification; a closed
        // option stamps resolved_at). This handler must never be the way around that.
        if (subtaskCheckboxDisabled(subtaskBoard, statusCol)) return;
        const optionId = done ? closedOptionId(statusCol) : openOptionId(statusCol);
        // No role-bearing option to land on ⇒ no write. Never guess an option id.
        if (!optionId) return;
        onPatchCell?.(rowId, statusCol.id, optionId);
      }}
      onSetAssignee={(rowId, userId) => {
        if (!personCol) return;
        // Person cells are a LIST of user uuids — never a bare string.
        onPatchCell?.(rowId, personCol.id, userId ? [userId] : []);
      }}
      onSetDue={(rowId, due) => {
        if (!dateCol) return;
        // Date cells hold a plain ISO string (or null), not a { date } object.
        onPatchCell?.(rowId, dateCol.id, due);
      }}
      onRename={(rowId, title) => {
        if (!titleCol) return;
        onPatchCell?.(rowId, titleCol.id, title);
      }}
      onOpen={(rowId) => onOpenRow?.(rowId)}
    />
  );
}
